import { useState, useMemo, useEffect } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { Check, Plus, ChevronsUpDown, Eye, FileText, Loader2 } from "lucide-react";
import { FilePreviewDialog } from "@/components/common/file-preview-dialog";
import { cn, FILE_TYPE_MAPPING } from "@/lib/utils";
import { useDealsListStore } from "@/store/deals-list-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { dealsApi } from "@/api/deals-api";
import { useUser } from "@clerk/clerk-react";
import { AddDealDialog } from "@/components/deals/add-deal";
import { CompanyAvatar } from "@/components/deals/company-avatar";
import { IInboundEmailAttachment, IInboundEmailExtractedInfo } from "@/types/data-models";
import type { DealTransformed } from "@/lib/schema/deal-schema";

type CompanyFile = DealTransformed["company_files"][number];

interface LinkToDealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLink: (
    dealId: string,
    companyId?: string,
    fileTypes?: Record<string, string>
  ) => void;
  isLinking?: boolean;
  emailSubject?: string;
  emailBody?: string;
  emailFrom?: string;
  attachments?: IInboundEmailAttachment[];
  extractedInfo?: IInboundEmailExtractedInfo;
}

export function LinkToDealDialog({
  open,
  onOpenChange,
  onLink,
  isLinking,
  emailSubject,
  emailBody,
  emailFrom,
  attachments,
  extractedInfo,
}: LinkToDealDialogProps) {
  const { deals, isLoading: isDealsLoading } = useDealsListStore();
  const { activeWorkspaceId } = useWorkspaceStore();
  const { user } = useUser();
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [step, setStep] = useState<"select-deal" | "assign-file-types">(
    "select-deal"
  );
  const [fileTypes, setFileTypes] = useState<Record<string, string>>({});
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  // Fresh `company_files` for the selected deal. Null until the
  // /fetch_single_deal call resolves — we fall back to the list-store's
  // cached copy in the meantime. The list store is populated once on app
  // load and can be stale (e.g. the user uploaded files into Data Room
  // AFTER the list was fetched), so the async refetch is how counts and
  // sublist reflect the current state.
  const [freshDealFiles, setFreshDealFiles] = useState<CompanyFile[] | null>(
    null
  );
  // Guards the "Next"/"Link" button on step 1 while the pre-step async work
  // (add email as deal note) is in flight. The parent's `isLinking` only
  // covers the final ingest mutation, so without this local flag the button
  // stays clickable and rapid clicks fire duplicate note inserts.
  const [isProceeding, setIsProceeding] = useState(false);

  const dealList = useMemo(
    () =>
      Object.values(deals).map((deal) => ({
        id: deal.id,
        companyName: deal.overview.name || "Unknown Company",
        companyId: deal.id,
        status: deal.overview.status,
        company_files: deal.company_files,
        website: deal.overview.website,
        companySource: deal.overview.companySource,
      })),
    [deals]
  );

  // Only attachments that have a file_upload_id can be type-assigned
  const linkableAttachments = useMemo(
    () => (attachments ?? []).filter((att) => att.file_upload_id),
    [attachments]
  );

  const hasAttachments = linkableAttachments.length > 0;

  // Cached files for the selected deal, derived synchronously from the
  // list store so we never flash a previous deal's data when switching.
  const cachedDealFiles = useMemo<CompanyFile[]>(() => {
    const cached = dealList.find((d) => d.id === selectedDealId);
    return cached?.company_files ?? [];
  }, [dealList, selectedDealId]);

  const dealCompanyFiles = freshDealFiles ?? cachedDealFiles;

  // Refetch the selected deal when the user enters the assign step so files
  // uploaded after the list-store was last fetched are reflected. Reset the
  // fresh copy immediately on deal/step change to avoid cross-deal leakage.
  useEffect(() => {
    setFreshDealFiles(null);
    if (step !== "assign-file-types") return;
    if (!activeWorkspaceId || !selectedDealId) return;
    let cancelled = false;
    dealsApi
      .getDealById(activeWorkspaceId, selectedDealId)
      .then((app) => {
        if (cancelled) return;
        setFreshDealFiles((app?.company_files ?? []) as CompanyFile[]);
      })
      .catch((err) => {
        console.error("Failed to refresh deal files", err);
      });
    return () => {
      cancelled = true;
    };
  }, [step, selectedDealId, activeWorkspaceId]);

  // Group the selected deal's existing files by `file_type`. Used to show
  // "(N)" counts in the file-type popover and an "Already on deal" sublist
  // below each attachment row once a type is picked.
  const filesByType = useMemo(() => {
    const map = new Map<string, CompanyFile[]>();
    for (const f of dealCompanyFiles) {
      if (!f.file_type) continue;
      const existing = map.get(f.file_type) ?? [];
      existing.push(f);
      map.set(f.file_type, existing);
    }
    // Sort each bucket newest-first for the inline sublist.
    for (const [, arr] of map) {
      arr.sort((a, b) => {
        const ad = a.time_stamp ? new Date(a.time_stamp).getTime() : 0;
        const bd = b.time_stamp ? new Date(b.time_stamp).getTime() : 0;
        return bd - ad;
      });
    }
    return map;
  }, [dealCompanyFiles]);

  const formatRelative = (ts?: string) => {
    if (!ts) return "";
    try {
      return formatDistanceToNow(new Date(ts), { addSuffix: true });
    } catch {
      return "";
    }
  };

  // Filenames on CompanyFile records may be percent-encoded (e.g. spaces
  // as %20). Decode for display so the user sees the real name. Mirrors
  // data-room.tsx which does the same. Falls back to file_type when the
  // filename is missing or malformed.
  const displayFileName = (f: CompanyFile) => {
    if (!f.file_name) return f.file_type;
    try {
      return decodeURIComponent(f.file_name);
    } catch {
      return f.file_type;
    }
  };

  const proceedToLink = (dealId: string, companyId?: string) => {
    if (hasAttachments) {
      setSelectedDealId(dealId);
      setStep("assign-file-types");
    } else {
      onLink(dealId, companyId);
    }
  };

  const handleLink = async () => {
    if (!selectedDealId || isProceeding) return;
    setIsProceeding(true);
    try {
      const deal = dealList.find((d) => d.id === selectedDealId);
      await addEmailAsDealNote(deal?.companyId || selectedDealId);
      proceedToLink(selectedDealId, deal?.companyId);
    } finally {
      setIsProceeding(false);
    }
  };

  const handleConfirmFileTypes = () => {
    if (!selectedDealId) return;
    const deal = dealList.find((d) => d.id === selectedDealId);
    onLink(selectedDealId, deal?.companyId || selectedDealId, fileTypes);
  };

  const addEmailAsDealNote = async (companyId: string) => {
    if (!activeWorkspaceId || !user) return;

    const noteContent = [
      emailFrom ? `From: ${emailFrom}` : "",
      emailSubject ? `Subject: ${emailSubject}` : "",
      "",
      emailBody || "",
    ]
      .filter(Boolean)
      .join("\n");

    try {
      await dealsApi.addDealNote(
        companyId,
        noteContent,
        activeWorkspaceId,
        user.fullName || user.primaryEmailAddress?.emailAddress || "System",
        ["email"]
      );
    } catch {
      // Non-blocking — deal was already created
      console.error("Failed to add email as deal note");
    }
  };

  const handleDealCreated = async (companyId: string) => {
    await addEmailAsDealNote(companyId);
    setAddDealOpen(false);
    proceedToLink(companyId, companyId);
  };

  const resetState = (open: boolean) => {
    if (!open) {
      setSelectedDealId(null);
      setStep("select-deal");
      setFileTypes({});
      setOpenPopoverId(null);
    }
    onOpenChange(open);
  };

  const allFilesTyped =
    linkableAttachments.length > 0 &&
    linkableAttachments.every((att) => fileTypes[att.file_upload_id!]);

  return (
    <>
      <Dialog open={open && !addDealOpen} onOpenChange={resetState}>
        <DialogContent className="sm:max-w-md">
          {step === "select-deal" && (
            <>
              <DialogHeader>
                <DialogTitle>Link to Deal</DialogTitle>
              </DialogHeader>
              <Command className="border rounded-lg">
                <CommandInput placeholder="Search deals..." />
                <CommandList className="max-h-64">
                  {isDealsLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <>
                  <CommandEmpty>No deals found.</CommandEmpty>
                  <CommandGroup>
                    {dealList.map((deal) => (
                      <CommandItem
                        key={deal.id}
                        value={deal.companyName}
                        disabled={Boolean(deal.companySource)}
                        onSelect={() => setSelectedDealId(deal.id)}
                        className="cursor-pointer"
                      >
                        <CompanyAvatar
                          companyFiles={deal.company_files}
                          websiteUrl={deal.website}
                          alt={deal.companyName}
                          className="h-6 w-6 flex-shrink-0 mr-2"
                          iconClassName="h-3.5 w-3.5"
                        />
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="font-medium truncate">
                            {deal.companyName}
                          </span>
                          {deal.companySource ? (
                            <span className="text-xs text-muted-foreground">
                              Can't link emails — added from Deal Radar
                            </span>
                          ) : (
                            deal.status && (
                              <span className="text-xs text-muted-foreground">
                                {deal.status}
                              </span>
                            )
                          )}
                        </div>
                        {selectedDealId === deal.id && (
                          <Check className="h-4 w-4 flex-shrink-0 text-primary" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>

              <Separator />

              <Button
                variant="outline"
                className="w-full justify-start gap-2"
                onClick={() => setAddDealOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Create New Deal
              </Button>

              <DialogFooter>
                <Button variant="outline" onClick={() => resetState(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleLink}
                  disabled={!selectedDealId || isLinking || isProceeding}
                >
                  {isLinking || isProceeding ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      {isLinking ? "Linking..." : "Loading..."}
                    </>
                  ) : hasAttachments ? (
                    "Next"
                  ) : (
                    "Link"
                  )}
                </Button>
              </DialogFooter>
            </>
          )}

          {step === "assign-file-types" && (
            <>
              <DialogHeader>
                <DialogTitle>Assign File Types</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Select a document type for each attachment to begin ingestion.
              </p>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {linkableAttachments.map((att) => {
                  const pickedValue = fileTypes[att.file_upload_id!];
                  const existing = pickedValue
                    ? filesByType.get(pickedValue) ?? []
                    : [];
                  return (
                  <div key={att.file_upload_id} className="space-y-1.5">
                  <div
                    className="flex items-center gap-3"
                  >
                    <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm truncate flex-1 min-w-0">
                      {att.file_name}
                    </span>
                    {att.file_url && (
                      <FilePreviewDialog
                        url={att.file_url}
                        fileName={att.file_name}
                        fileId={att.file_upload_id}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-foreground"
                          aria-label="View file"
                          title="View file"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </FilePreviewDialog>
                    )}
                    <Popover
                      open={openPopoverId === att.file_upload_id}
                      onOpenChange={(isOpen) =>
                        setOpenPopoverId(isOpen ? att.file_upload_id! : null)
                      }
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[180px] justify-between font-normal text-sm h-8 flex-shrink-0"
                        >
                          <span className="truncate">
                            {fileTypes[att.file_upload_id!]
                              ? Object.entries(FILE_TYPE_MAPPING).find(
                                  ([, v]) =>
                                    v === fileTypes[att.file_upload_id!]
                                )?.[0] || "Select type"
                              : "Select type"}
                          </span>
                          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[240px] p-0 z-[200]"
                        align="end"
                        sideOffset={4}
                        collisionPadding={12}
                      >
                        <Command>
                          <CommandInput
                            placeholder="Search type..."
                            className="h-8"
                          />
                          <CommandList className="max-h-[260px]">
                            <CommandEmpty>No type found.</CommandEmpty>
                            <CommandGroup>
                              {Object.entries(FILE_TYPE_MAPPING).map(
                                ([label, value]) => {
                                  const count =
                                    filesByType.get(value)?.length ?? 0;
                                  return (
                                    <CommandItem
                                      key={value}
                                      value={label}
                                      onSelect={() => {
                                        setFileTypes((prev) => ({
                                          ...prev,
                                          [att.file_upload_id!]: value,
                                        }));
                                        setOpenPopoverId(null);
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-3 w-3",
                                          fileTypes[att.file_upload_id!] ===
                                            value
                                            ? "opacity-100"
                                            : "opacity-0"
                                        )}
                                      />
                                      <span className="flex-1 truncate">
                                        {label}
                                      </span>
                                      {count > 0 && (
                                        <TooltipProvider
                                          delayDuration={200}
                                        >
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span className="ml-2 inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                                {count} on deal
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="left"
                                              className="z-[300] text-xs"
                                            >
                                              {count}{" "}
                                              {count === 1
                                                ? "file"
                                                : "files"}{" "}
                                              of this type already attached
                                              to the deal
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </CommandItem>
                                  );
                                }
                              )}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  {pickedValue && existing.length > 0 && (
                    <div className="pl-7 space-y-1">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                        Already on deal ({existing.length})
                      </p>
                      <div className="flex flex-col gap-0.5">
                        {existing.slice(0, 3).map((f) => {
                          const displayName = displayFileName(f);
                          return (
                            <FilePreviewDialog
                              key={f._id}
                              url={f.file_url}
                              fileName={displayName}
                              fileId={f._id}
                            >
                              <button
                                type="button"
                                className="group/row flex w-full min-w-0 items-center gap-2 rounded px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                aria-label={`View existing ${displayName}`}
                                title={displayName}
                              >
                                <FileText className="h-3 w-3 shrink-0" />
                                <span
                                  className="min-w-0 flex-1 truncate text-left"
                                >
                                  {displayName}
                                </span>
                                <span className="shrink-0 text-[10px] text-muted-foreground/70">
                                  {formatRelative(f.time_stamp)}
                                </span>
                                <Eye className="h-3 w-3 shrink-0 text-muted-foreground/40 transition-colors group-hover/row:text-foreground" />
                              </button>
                            </FilePreviewDialog>
                          );
                        })}
                        {existing.length > 3 && (
                          <span className="pl-1.5 text-[10px] text-muted-foreground/70">
                            +{existing.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  </div>
                  );
                })}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setStep("select-deal")}
                >
                  Back
                </Button>
                <Button
                  onClick={handleConfirmFileTypes}
                  disabled={!allFilesTyped || isLinking}
                >
                  {isLinking ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Linking...
                    </>
                  ) : (
                    "Link & Ingest"
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AddDealDialog
        open={addDealOpen}
        onOpenChange={(open) => {
          setAddDealOpen(open);
          if (!open) {
            // Re-show the link dialog when add deal is closed without creating
          }
        }}
        showTrigger={false}
        onDealCreated={handleDealCreated}
        initialValues={extractedInfo ? {
          companyName: extractedInfo.company_name && extractedInfo.company_name !== "N/A" ? extractedInfo.company_name : undefined,
          website: extractedInfo.website && extractedInfo.website !== "N/A" ? extractedInfo.website : undefined,
        } : undefined}
      />
    </>
  );
}
